import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import toast, { Toaster } from "react-hot-toast";
import { exchangeCodeForTokens, exchangeGoogleAuthCode, generateOtp, signIn } from "../../services/authServices";
import { setLogId, setUserDetails, userPreferences, setCustomSource } from "../../actions/actions";
import "./onboarding.css";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import googleIcon from "../../assets/images/google-logo.svg";
import facebookIcon from "../../assets/images/facebook-logo.svg";
import { gapi } from "gapi-script";

import logger, { Logger } from '../../utils/logger';
import { getDeviceInfo } from "../../utils/deviceInfo";

interface LoginProps {
  onSwitchToMpin?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSwitchToMpin }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("91");
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastRequestTime, setLastRequestTime] = useState<number>(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Only check for auth token
    const authToken = localStorage.getItem("auth_token");
    if (authToken) {
      navigate("/leads", { replace: true });
    }
  }, [navigate]);

 
 

  const validatePhoneNumber = useCallback((number: string): boolean => {
    const phoneNumberPattern = /^[1-9]\d{9}$/;
    return phoneNumberPattern.test(number);
  }, []);

  const handlePhoneChange = useCallback(
    (value: string, country: any) => {
      const newCountryCode = country.dialCode;
      const numberWithoutCode = value.slice(newCountryCode.length);

      setPhoneNumber(numberWithoutCode);
      setCountryCode(newCountryCode);
      setIsValid(validatePhoneNumber(numberWithoutCode));
    },
    [validatePhoneNumber]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!validatePhoneNumber(phoneNumber)) {
        setIsValid(false);
        return;
      }

      const currentTime = Date.now();
      if (currentTime - lastRequestTime < 60000) {
        toast.error("Please wait a moment before requesting another OTP");
        return;
      }

      setIsLoading(true);

      try {
        const response = await generateOtp({
          phone: phoneNumber,
          countryCode: countryCode,
         otpType: "custom_sms_otp",
        });

        if (response && response.status) {
          localStorage.setItem("phone", phoneNumber);
          localStorage.setItem("countryCode", countryCode);
          toast.success("OTP Sent Successfully!");
          dispatch(setLogId(response?.data?.data?.LogID));
          setLastRequestTime(currentTime);
          navigate("./otp-verify");
        }
      } catch (error) {
        console.error("Error in API call:", error);
        toast.error(
          "Error occurred while sending OTP. Please try again later."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      phoneNumber,
      countryCode,
      lastRequestTime,
      validatePhoneNumber,
      navigate,
      dispatch,
    ]
  );


  const responseGoogle = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        Logger.log("Google Code Response:", codeResponse);
        
        // Exchange the code for tokens (including id_token)
        const tokens = await exchangeCodeForTokens(codeResponse.code);
        Logger.log("Google Tokens:", tokens);
        
        // Get user info using the access token
        const userInfoResponse = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokens.access_token}` },
          }
        );
        
        Logger.log("Google User Info:", userInfoResponse.data);

        // Store user info for signup pre-population
        localStorage.setItem("email", userInfoResponse.data.email);
        localStorage.setItem("userName", userInfoResponse.data.name || userInfoResponse.data.given_name || "");

      const data = {
  loginType: "google",
  loggedFrom: "web",
  deviceToken: JSON.parse(localStorage.getItem('fcm_token_data') || '{}').token,
  identityToken: tokens.id_token,
  deviceInfo: await getDeviceInfo(),
};
        
        Logger.log("Data being sent to signIn:", data);
        
        const resp = await signIn(data);
        Logger.log("Sign In Response:", resp);
        
        if (resp && resp.status) {
          localStorage.setItem("auth_token", resp?.data?.data?.token);
          localStorage.setItem("userData", JSON.stringify(resp?.data?.data));
          
          // Dispatch user details to Redux (same as OTP login)
          dispatch(setUserDetails(resp?.data?.data));
          if (resp?.data?.data?.userPreference) {
            dispatch(userPreferences(resp.data.data.userPreference));
            dispatch(setCustomSource(resp.data.data.userPreference.customSource));
          }

          // Store businesses
          if (resp?.data?.data?.multiAccountList?.length) {
            localStorage.setItem(
              "userBusinesses",
              JSON.stringify(resp.data.data.multiAccountList)
            );
          }

          // Store user preferences
          if (resp?.data?.data?.userPreference) {
            localStorage.setItem(
              "user_preferences",
              JSON.stringify(resp.data.data.userPreference)
            );
          }

          logger.log("🔑 SOCIAL LOGIN - Data stored:", {
  timestamp: new Date().toISOString(),
  loginType: 'google/facebook',
  userData: resp?.data?.data,
  hasUserPreference: !!resp?.data?.data?.userPreference,
  userDetailsType: Array.isArray(resp?.data?.data) ? 'ARRAY' : 'OBJECT'
});

          if (resp?.data?.data?.isOnboardCompleted) {
            if (resp?.data?.data?.isSubscriptionActive) {
              toast.success("Logged In Successfully!");
              navigate("/leads", { replace: true });
            } else {
              // Updated to navigate to SubscriptionExpiredPage instead of showing toast error
              navigate("/SubscriptionExpiredPage", { replace: true });
            }
          } else {
            navigate("./sign-up");
          }
        }
      } catch (err: any) {
        console.error("Login Error:", err);
        // Check if the error indicates an expired subscription (403 status)
        if (err.response && err.response.status === 403 && err.response.data) {
          const errorData = err.response.data.data;
          // Display the actual error message from backend
          const errorMessage = err.response.data.message || "Error occurred while logging in!";

          // Check for subscription expiry
          if (errorData?.isSubscriptionExpired || errorData?.isFreePlan) {
            toast.error(errorMessage);
            navigate("/SubscriptionExpiredPage", { replace: true });
          } else {
            toast.error(errorMessage);
          }
        } else {
          // Display backend error message if available, otherwise generic message
          const errorMessage = err.response?.data?.message || "Error occurred while logging in!";
          toast.error(errorMessage);
        }
      }
    },
    onError: (error) => {
      console.error("Google Login Error:", error);
      toast.error("Error occurred while logging in!");
    },
    flow: "auth-code",
    scope: "openid email profile",
  });


  
  const responseFacebook = async (response: any) => {
    Logger.log("facebook response", response);

    if (response.accessToken) {
      try {
        // Store user info for signup pre-population
        if (response.email) {
          localStorage.setItem("email", response.email);
        }
        if (response.name) {
          localStorage.setItem("userName", response.name);
        }

        const body = {
          loggedFrom: "web",
          identityToken: response.accessToken,
          deviceToken: JSON.parse(localStorage.getItem('fcm_token_data') || '{}').token,
          loginType: "facebook",
           deviceInfo: await getDeviceInfo(),
        };

        const resp = await signIn(body);
        if (resp && resp.status) {
          localStorage.setItem("auth_token", resp?.data?.data?.token);
          localStorage.setItem("userData", JSON.stringify(resp?.data?.data));

          // Dispatch user details to Redux (same as OTP login)
          dispatch(setUserDetails(resp?.data?.data));
          if (resp?.data?.data?.userPreference) {
            localStorage.setItem("user_preferences", JSON.stringify(resp.data.data.userPreference));
            dispatch(userPreferences(resp.data.data.userPreference));
            dispatch(setCustomSource(resp.data.data.userPreference.customSource));
          }
          if (resp?.data?.data?.multiAccountList?.length) {
            localStorage.setItem("userBusinesses", JSON.stringify(resp.data.data.multiAccountList));
          }

          if (resp?.data?.data?.isOnboardCompleted) {
            if (resp?.data?.data?.isSubscriptionActive) {
              toast.success("Logged In Successfully!");
              navigate("/leads", { replace: true });
            } else {
              // This was already correctly redirecting to SubscriptionExpiredPage
              navigate("/SubscriptionExpiredPage", { replace: true });
            }
          } else {
            navigate("./sign-up");
          }
        }
      } catch (err: any) {
        // Check if the error indicates an expired subscription (403 status)
        if (err.response && err.response.status === 403 && err.response.data) {
          const errorData = err.response.data.data;
          // Display the actual error message from backend
          const errorMessage = err.response.data.message || "Error occurred while logging in!";

          // Check for subscription expiry
          if (errorData?.isSubscriptionExpired || errorData?.isFreePlan) {
            toast.error(errorMessage);
            navigate("/SubscriptionExpiredPage", { replace: true });
          } else {
            toast.error(errorMessage);
          }
        } else {
          // Display backend error message if available, otherwise generic message
          const errorMessage = err.response?.data?.message || "Error occurred while logging in!";
          toast.error(errorMessage);
        }
      }
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        {/* Logo */}
        <div className="onboarding-logo">
          <img src="assets/img/3sigma_logo.png" alt="3Sigma CRM" />
        </div>

        {/* Header */}
        <div className="onboarding-header">
          <h2>Welcome Back</h2>
          <p className="onboarding-subtitle">
            Enter your phone number to receive a verification code
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="onboarding-form">
          <div className="onboarding-form-group">
            <div className="onboarding-phone-container">
              <PhoneInput
                country="in"
                value={`${countryCode}${phoneNumber}`}
                onChange={handlePhoneChange}
                inputClass={!isValid ? "is-invalid" : ""}
                containerClass="react-tel-input"
              />
              {!isValid && (
                <span className="onboarding-error-text">
                  Please enter a valid mobile number
                </span>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="onboarding-btn-primary"
            disabled={isLoading || !isValid}
          >
            {isLoading ? (
              <span className="onboarding-loading">
                <span className="onboarding-spinner"></span>
                Sending Code...
              </span>
            ) : (
              "Get OTP Code"
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="onboarding-divider">
          <span className="onboarding-divider-text">or continue with</span>
        </div>

        {/* Social Login */}
        <div className="onboarding-social-container">
          <FacebookLogin
            appId="620404531989611"
            autoLoad={false}
            fields="name,email,picture"
            callback={responseFacebook}
            render={(renderProps) => (
              <div
                className="onboarding-social-button"
                onClick={renderProps.onClick}
              >
                <img src={facebookIcon} alt="Facebook" />
              </div>
            )}
          />

          <div
            className="onboarding-social-button"
            onClick={() => responseGoogle()}
          >
            <img src={googleIcon} alt="Google" />
          </div>

          {onSwitchToMpin && (
            <button
              className="onboarding-social-button mpin"
              onClick={onSwitchToMpin}
              type="button"
            >
              <span>PIN</span>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="onboarding-footer">
          <p className="onboarding-footer-text">
            By logging in, you agree to our{" "}
            <a
              href="https://www.3sigmacrm.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>{" "}
            and{" "}
            <a
              href="https://www.3sigmacrm.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
      <Toaster position="top-right" reverseOrder={false} />
    </div>
  );
};

export default Login;